import { ChakraProps, Image } from '@chakra-ui/react';
import {
    library, findIconDefinition, IconDefinition, IconName, SizeProp,
} from '@fortawesome/fontawesome-svg-core';
import {
    faCalendarCheck,
    faCircleInfo,
    faClipboardCheck,
    faClipboardList,
    faEnvelope,
    faFileInvoice,
    faFileInvoiceDollar,
    faFileLines,
    faHouse,
    faPenToSquare,
    faRocket,
    faShieldExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { NamedIconReference } from '@acp/shared';
import React from 'react';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { getBrandIconByName } from '../../../assets';
import { FaIcon } from '../fa-icon/fa-icon';

// FA icons being used by this component will need to be manually added here.
const preloadedIcons = [
    faRocket,
    // Used by the property timeline (activity.icon is a NamedIconReference).
    faCalendarCheck,
    faFileInvoiceDollar,
    faHouse,
    faFileLines,
    faClipboardCheck,
    faCircleInfo,
    faEnvelope,
    faShieldExclamation,
    faPenToSquare,
    faClipboardList,
    faFileInvoice,
    // Add more as required...
] as const;

export function setupFaIconLibrary() {
    library.add(...preloadedIcons);
}

function NamedBrandIconRenderer({
    ref,
    iconName,
    size = 'lg',
    ...props
}: {
    ref: React.ForwardedRef<SVGSVGElement | HTMLImageElement>;
    iconName: string;
    size?: SizeProp;
}) {
    const src = getBrandIconByName(iconName);

    // These sizes match the named sizes used in FaIcon
    const brandIconBoxSize: Record<string, string> = {
        xs: '12px',
        sm: '16px',
        md: '20px',
        lg: '24px',
        xl: '32px',
    };

    if (src === null) {
        return null;
    }

    return (
        <Image
            {...props}
            src={src}
            boxSize={brandIconBoxSize[size]}
            ref={ref as React.Ref<HTMLImageElement>}
        />
    );
}

function NamedFaIconRenderer({
    ref,
    iconName,
    color,
    size,
    ...props
}: Omit<ChakraProps, 'color'> & {
    ref: React.ForwardedRef<SVGSVGElement | HTMLImageElement>;
    iconName: string;
    color?: string;
    size?: SizeProp;
}) {
    const definition = findIconDefinition({
        // This component currently only supports /pro-light-svg-icons
        prefix: 'fal',
        iconName: iconName as IconName,
    }) as IconDefinition | undefined;

    if (definition === undefined) {
        return null;
    }

    return (
        <FaIcon
            {...props as ChakraProps & Omit<FontAwesomeIconProps, 'icon'>}
            icon={definition}
            color={color}
            size={size}
            ref={ref as React.Ref<SVGSVGElement>}
        />
    );
}

export const NamedIcon = React.forwardRef<SVGSVGElement | HTMLImageElement, Omit<ChakraProps, 'color'> & {
    icon: NamedIconReference;
    size?: SizeProp;
    color?: string;
}>((
    {
        icon,
        size,
        color,
        ...props
    },
    ref
) => {
    if (icon.source === 'brand') {
        return (
            <NamedBrandIconRenderer
                {...props}
                ref={ref}
                // See assets.ts for the list of usable brandIcons
                iconName={icon.name}
                size={size}
            />
        );
    }

    return (
        <NamedFaIconRenderer
            {...props}
            ref={ref}
            iconName={icon.name}
            color={color}
            size={size}
        />
    );
});
